import { supabase } from '../../supabase';
import { Paper } from '@/types/paper';
import { mapToPaper } from '../paper-mappers';
import { toast } from 'sonner';

/**
 * Get all papers authored by the current user (both public and private)
 * Includes engagement metrics: views, review counts, comment counts
 * @param userId User ID to fetch papers for
 * @returns Object with publicPapers and privatePapers arrays
 */
export async function getUserAuthoredPapers(userId: string): Promise<{
  publicPapers: Paper[];
  privatePapers: Paper[];
}> {
  try {
    if (!userId || typeof userId !== 'string') {
      console.error('Invalid user ID:', userId);
      return { publicPapers: [], privatePapers: [] };
    }
    
    console.log('Fetching authored papers for user ID:', userId);
    
    // Get paper IDs where user is the author_id OR a claimed author
    const { data: paperIds, error: paperIdsError } = await supabase
      .rpc('get_papers_by_author', { author_uuid: userId });

    if (paperIdsError) {
      console.error('Error fetching paper IDs:', paperIdsError);
      toast.error('Failed to load your papers');
      return { publicPapers: [], privatePapers: [] };
    }

    if (!paperIds || paperIds.length === 0) {
      console.log('No papers found for user');
      return { publicPapers: [], privatePapers: [] };
    }

    const ids = paperIds.map((p: { paper_id: string }) => p.paper_id);

    // Fetch all paper details
    const { data: papers, error: papersError } = await supabase
      .from('papers')
      .select('*')
      .in('id', ids)
      .order('updated_at', { ascending: false });

    if (papersError) {
      console.error('Error fetching papers:', papersError);
      toast.error('Failed to load paper details');
      return { publicPapers: [], privatePapers: [] };
    }

    // Fetch versions
    const { data: versions, error: versionsError } = await supabase
      .from('paper_versions')
      .select('*')
      .in('paper_id', ids);

    if (versionsError) {
      console.error('Error fetching versions:', versionsError);
    }

    // Group versions by paper_id
    const versionsByPaperId = (versions || []).reduce((acc, version) => {
      if (!acc[version.paper_id]) {
        acc[version.paper_id] = [];
      }
      acc[version.paper_id].push(version);
      return acc;
    }, {} as Record<string, any[]>);

    // Fetch engagement metrics (review counts and comment counts)
    const [reviewCounts, commentCounts] = await Promise.all([
      supabase
        .from('reviews')
        .select('paper_id')
        .in('paper_id', ids)
        .eq('status', 'published'),
      supabase
        .from('comments')
        .select('paper_id')
        .in('paper_id', ids)
    ]);

    // Count reviews and comments per paper
    const reviewCountByPaper = (reviewCounts.data || []).reduce((acc, r) => {
      acc[r.paper_id] = (acc[r.paper_id] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const commentCountByPaper = (commentCounts.data || []).reduce((acc, c) => {
      acc[c.paper_id] = (acc[c.paper_id] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Map papers and separate by visibility
    const mappedPapers = (papers || []).map(paper => {
      const mappedPaper = mapToPaper(paper, versionsByPaperId[paper.id] || []);
      
      // Add engagement metrics (not part of Paper type, but added for display)
      (mappedPaper as any).reviewCount = reviewCountByPaper[paper.id] || 0;
      (mappedPaper as any).commentCount = commentCountByPaper[paper.id] || 0;
      
      return mappedPaper;
    });

    const publicPapers = mappedPapers.filter(p => p.visibility === 'public');
    const privatePapers = mappedPapers.filter(p => p.visibility === 'private');

    console.log(`Fetched ${publicPapers.length} public and ${privatePapers.length} private papers`);
    
    return { publicPapers, privatePapers };
  } catch (error: any) {
    console.error('Error in getUserAuthoredPapers:', error);
    toast.error('Failed to load your papers');
    return { publicPapers: [], privatePapers: [] };
  }
}
